// src/.../generateAdmissionForm.js
import jsPDF from "jspdf";
import api from "../../api";

/**
 * Load image as base64 via backend proxy to avoid CORS issues
 * Backend route: /api/image_proxy?url=...
 */
const loadImage = async (url) => {
  if (!url) return null;
  try {
    const res = await api.get("/image_proxy", {
      params: { url },          // backend e urldecode hobe
      responseType: "text",     // base64 string, JSON na
    });
    return res.data || null;
  } catch (err) {
    console.error("Image load failed:", err);
    return null;
  }
};

const generateAdmissionForm = async (data) => {
  const doc = new jsPDF("p", "mm", "a4");

  const student = data || {};
  const address = student.address || {};
  const bank = student.bank || {};
  const fees = Array.isArray(student.fees) ? student.fees : [];
  const school = Array.isArray(student.school) ? student.school[0] || {} : {};

  // ---------- Load school logo & student photo ----------
  const logoURL = school.logo
    ? `https://dgps.co.in/public/admin/assets/img/schools/logo/${school.logo}`
    : null;

  const [logoImg, studentPhoto] = await Promise.all([
    loadImage(logoURL),
    loadImage(student.file),
  ]);

  const drawLine = (y) => doc.line(20, y, 190, y);

  /* ============= NAVY HEADER (CORPORATE LOOK) ============= */
  doc.setFillColor(0, 33, 64); // #002140
  doc.rect(10, 10, 190, 32, "F");

  if (logoImg) {
    doc.addImage(logoImg, "PNG", 16, 13, 22, 22);
  }

  // School name
  doc.setFont("helvetica", "bold");
  doc.setFontSize(17);
  doc.setTextColor(255, 255, 255);
  doc.text(school.name || "SCHOOL NAME", 105, 22, { align: "center" });

  // Address + contact
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.5);
  const addrLine = school.address || "";
  const contactLine = [
    school.phone ? `Phone: ${school.phone}` : "",
    school.email ? `Email: ${school.email}` : "",
  ]
    .filter(Boolean)
    .join("  |  ");

  doc.text(addrLine, 105, 28, { align: "center" });
  if (contactLine) doc.text(contactLine, 105, 33, { align: "center" });

  // Thin underline
  doc.setDrawColor(255, 255, 255);
  doc.setLineWidth(0.3);
  doc.line(20, 38, 190, 38);

  /* ===================== FORM TITLE BADGE ===================== */
  doc.setFillColor(0, 90, 170);
  doc.rect(70, 45, 70, 9, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(255, 255, 255);
  doc.text("ADMISSION FORM", 105, 51, { align: "center" });

  // Reset text color
  doc.setTextColor(0, 0, 0);

  /* ===================== PHOTO PANEL ===================== */
  doc.setFillColor(245, 247, 252);
  doc.roundedRect(150, 55, 45, 55, 2, 2, "F");
  doc.setDrawColor(200, 205, 215);
  doc.roundedRect(150, 55, 45, 55, 2, 2);

  if (studentPhoto) {
    doc.addImage(studentPhoto, "JPEG", 153, 58, 39, 48);
  } else {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(9);
    doc.text("Photo", 172, 83, { align: "center" });
  }

  /* ===================== MAIN CONTENT ===================== */
  let y = 60;

  const drawSectionTitle = (title) => {
    y += 10;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(0, 33, 64);
    doc.text(title, 20, y);
    doc.setDrawColor(0, 33, 64);
    doc.setLineWidth(0.2);
    doc.line(20, y + 1.5, 190, y + 1.5);
    y += 4;
    doc.setTextColor(0, 0, 0);
  };

  const addField = (label, value) => {
    y += 6.5;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9.5);
    doc.text(label, 22, y);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    doc.text(value || "-", 60, y);
  };

  /* -------- Student Information -------- */
  drawSectionTitle("Student Information");

  addField("Name:", (student.name || "").toUpperCase());
  addField("Father's Name:", (student.father_name || "").toUpperCase());
  addField("Mother's Name:", (student.mother_name || "").toUpperCase());
  addField("Gender:", student.gender || "-");
  addField("Date of Birth:", student.dob || "-");
  addField("Aadhaar No:", student.aadhaar_no || "-");
  addField("Blood Group:", student.blood_group || "-");
  addField("Mobile:", student.mobile || "-");

  /* -------- Address -------- */
  drawSectionTitle("Address");

  addField("Village:", (address.village || "").toUpperCase());
  addField("Post Office:", (address.post_office || "").toUpperCase());
  addField("Police Station:", (address.police_station || "").toUpperCase());
  addField("District:", (address.city || "").toUpperCase());
  addField("State:", (address.state || "").toUpperCase());
  addField("Block:", (address.block || "").toUpperCase());
  addField("Pin Code:", address.pincode || "-");

  /* -------- Academic Details -------- */
  drawSectionTitle("Academic Details");

  addField("Class:", student.class_name || "-");
  addField("Section:", student.section_name || "-");
  addField("Roll No:", student.roll_no || "-");

  /* -------- Fees Table -------- */
  drawSectionTitle("Fees Details");

  y += 4;
  doc.setFontSize(9.5);

  // table header
  doc.setFillColor(230, 236, 246);
  doc.setDrawColor(210, 216, 228);
  doc.rect(20, y, 170, 7, "FD");

  doc.setFont("helvetica", "bold");
  doc.text("Fee Name", 24, y + 4.5);
  doc.text("Amount (₹)", 165, y + 4.5, { align: "right" });

  y += 8;
  doc.setFont("helvetica", "normal");

  let total = 0;

  if (!fees.length) {
    doc.text("-", 24, y + 4);
  } else {
    fees.forEach((f) => {
      const name = f.fee_name || "-";
      const amt = parseFloat(f.amount || 0);
      total += isNaN(amt) ? 0 : amt;

      doc.setDrawColor(235, 235, 235);
      doc.line(20, y + 4.5, 190, y + 4.5);

      doc.text(name, 24, y + 4);
      doc.text(amt.toFixed(2), 165, y + 4, { align: "right" });

      y += 6;
    });
  }

  // total row
  y += 3;
  doc.setDrawColor(0, 33, 64);
  drawLine(y);
  y += 5;

  doc.setFont("helvetica", "bold");
  doc.text("TOTAL", 24, y);
  doc.text(total.toFixed(2), 165, y, { align: "right" });

  // নিচে আর কিছু নাই – A4 height er moddhei thakar কথা

  doc.save(`${student.student_code || student.name || "Admission_Form"}.pdf`);
};

export default generateAdmissionForm;
